/**
 * Utility to calculate which steps are visible in the onboarding progress bar
 * based on the user's role and access method.
 *
 * All possible steps (6 total):
 * - ROLE_SELECTION - Always shown
 * - COMPLETE_ACCOUNT - Always shown (fields within are conditional based on role)
 * - ACTIVATION_CODE - Always shown
 * - SELECT_PLAN - Skipped if activation code valid
 * - INVITE_FAMILY - Only if paid via Stripe
 * - ACCOUNT_CREATION_LOADING - Final loading page (virtual step)
 */

import { StepKey } from './stepNavigation';

/** Step keys including the virtual loading step */
export type OnboardingStepKey = StepKey | 'ACCOUNT_CREATION_LOADING';

export type VisibleStep = Readonly<{
  key: OnboardingStepKey;
}>;

export type AccessMethod = 'none' | 'activation_code' | 'paid_subscription' | 'family_membership';

type GetVisibleStepsParams = Readonly<{
  selectedRole: string | null;
  activationCodeValid: boolean | null;
  accessMethod: AccessMethod;
  isPaywallEnabled: boolean;
  isFamilyAccountsEnabled: boolean;
}>;

/**
 * Returns the list of steps that should be visible in the progress bar
 * based on the user's current path through onboarding.
 */
export function getVisibleSteps({
  // selectedRole: _selectedRole,
  activationCodeValid,
  accessMethod,
  isPaywallEnabled,
  isFamilyAccountsEnabled,
}: GetVisibleStepsParams): VisibleStep[] {
  const steps: VisibleStep[] = [];
  const canInviteFamily =
    isFamilyAccountsEnabled && isPaywallEnabled && accessMethod === 'paid_subscription';

  // ROLE_SELECTION - Always shown
  steps.push({ key: 'ROLE_SELECTION' });

  // COMPLETE_ACCOUNT - Always shown (fields within are conditional based on role)
  steps.push({ key: 'COMPLETE_ACCOUNT' });

  // ACTIVATION_CODE - Skipped if user is auto-accepted into family
  if (accessMethod !== 'family_membership') {
    steps.push({ key: 'ACTIVATION_CODE' });
  }

  // SELECT_PLAN - Skipped if paywall disabled OR activation code was valid OR family member
  if (isPaywallEnabled && activationCodeValid !== true && accessMethod !== 'family_membership') {
    steps.push({ key: 'SELECT_PLAN' });
  }

  // INVITE_FAMILY - Only if family_accounts enabled AND paywall enabled AND paid via Stripe
  if (canInviteFamily) {
    steps.push({ key: 'INVITE_FAMILY' });
  }

  // ACCOUNT_CREATION_LOADING - Always shown as final step
  steps.push({ key: 'ACCOUNT_CREATION_LOADING' });

  return steps;
}

export type OnboardingView =
  | 'questions'
  | 'subscription'
  | 'subscription-success'
  | 'invite-family'
  | 'account-creation-loading';

/**
 * Finds the progress step index for a given step key or view.
 * Returns the 0-based index within the visible steps array.
 */
export function getProgressStepIndex(
  visibleSteps: VisibleStep[],
  currentStep: StepKey,
  currentView: OnboardingView,
): number {
  if (currentView === 'account-creation-loading') {
    // Loading page is always the last step
    return visibleSteps.length - 1;
  }

  if (currentView === 'subscription' || currentView === 'subscription-success') {
    // Subscription view corresponds to SELECT_PLAN
    const idx = visibleSteps.findIndex((s) => s.key === 'SELECT_PLAN');

    return idx >= 0 ? idx : visibleSteps.length - 1;
  }

  if (currentView === 'invite-family') {
    // Invite family view corresponds to INVITE_FAMILY
    const idx = visibleSteps.findIndex((s) => s.key === 'INVITE_FAMILY');

    return idx >= 0 ? idx : visibleSteps.length - 1;
  }

  // For questions view, find the step by step key
  const idx = visibleSteps.findIndex((s) => s.key === currentStep);

  return idx >= 0 ? idx : 0;
}
